import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Upload, FileText, Loader2, Globe, MapPin, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export const JobCreationPanel = ({ onJobCreated, isLoading, initialData }) => {
  const [formData, setFormData] = useState({
    title: initialData?.title || '',
    job_type: initialData?.job_type || '',
    city: initialData?.city || '',
    country: initialData?.country || '',
    timezone: initialData?.timezone || '',
    job_description: initialData?.job_description || '',
    client_name: initialData?.client_name || '',
  });
  const [uploading, setUploading] = useState(false);

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    
    try {
      const text = await file.text();
      setFormData(prev => ({ ...prev, job_description: text }));
      toast.success('Job description loaded from file');
    } catch (error) {
      toast.error('Failed to read file');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!formData.title.trim()) {
      toast.error('Job title is required');
      return;
    }
    if (!formData.job_type) {
      toast.error('Job type is required');
      return;
    }
    if (!formData.job_description.trim()) {
      toast.error('Job description is required');
      return;
    }
    if ((formData.job_type === 'onsite' || formData.job_type === 'hybrid') && !formData.city && !formData.country) {
      toast.error('Location is required for onsite/hybrid roles');
      return;
    }

    onJobCreated(formData);
  };

  const showLocation = formData.job_type === 'onsite' || formData.job_type === 'hybrid';
  const charCount = formData.job_description.length;

  return (
    <Card className="border-2 border-dashed border-primary/20 hover:border-primary/40 transition-colors" data-testid="job-creation-panel">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-xl" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <FileText className="h-5 w-5 text-primary" strokeWidth={1.5} />
          Create New Job Analysis
        </CardTitle>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="title">Job Title *</Label>
              <Input
                id="title"
                data-testid="job-title-input"
                placeholder="e.g., Senior Software Engineer"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="job_type">Job Type *</Label>
              <Select 
                value={formData.job_type} 
                onValueChange={(value) => handleInputChange('job_type', value)}
              >
                <SelectTrigger id="job_type" data-testid="job-type-select">
                  <SelectValue placeholder="Select job type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="remote">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" /> Remote
                    </div>
                  </SelectItem>
                  <SelectItem value="onsite">
                    <div className="flex items-center gap-2">
                      <MapPin className="h-4 w-4" /> Onsite
                    </div>
                  </SelectItem>
                  <SelectItem value="hybrid">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4" /> Hybrid
                    </div>
                  </SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="client_name">Client Name</Label>
              <Input
                id="client_name"
                data-testid="client-name-input"
                placeholder="e.g., Acme Corp"
                value={formData.client_name}
                onChange={(e) => handleInputChange('client_name', e.target.value)}
              />
            </div>
          </div>

          {showLocation && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 animate-fadeIn">
              <div className="space-y-2">
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  data-testid="city-input"
                  placeholder="e.g., San Francisco"
                  value={formData.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  data-testid="country-input"
                  placeholder="e.g., USA"
                  value={formData.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="timezone">Timezone (Optional)</Label>
                <Input
                  id="timezone"
                  data-testid="timezone-input"
                  placeholder="e.g., PST"
                  value={formData.timezone}
                  onChange={(e) => handleInputChange('timezone', e.target.value)}
                />
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label htmlFor="job_description">Job Description *</Label>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">{charCount} characters</span>
                <label htmlFor="jd-file-upload" className="cursor-pointer">
                  <div className="inline-flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors">
                    <Upload className="h-3 w-3" />
                    {uploading ? 'Loading...' : 'Upload File'}
                  </div>
                  <input
                    id="jd-file-upload"
                    type="file"
                    accept=".txt,.pdf,.doc,.docx"
                    className="hidden"
                    onChange={handleFileUpload}
                    data-testid="jd-file-upload"
                  />
                </label>
              </div>
            </div>
            <Textarea
              id="job_description"
              data-testid="job-description-textarea"
              placeholder="Paste or type the job description here..."
              value={formData.job_description}
              onChange={(e) => handleInputChange('job_description', e.target.value)}
              className="min-h-[200px] resize-y"
            />
          </div>

          <Button 
            type="submit" 
            className="w-full"
            disabled={isLoading}
            data-testid="create-job-btn"
          >
            {isLoading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Creating Job...
              </>
            ) : (
              <>
                <FileText className="mr-2 h-4 w-4" />
                Create Job & Analyze JD
              </>
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
};

export default JobCreationPanel;
